import { useState, useEffect, useCallback } from "react";
import { Plus, Trash2, Pencil, Loader2, Save, X, Eye, EyeOff } from "lucide-react";
import { api } from "../../lib/api";

const emptyForm = { title: "", summary: "", body: "", tag: "", date: "", published: true, source_lang: "en" };

function pick(item) {
  const tr = item.translations || {};
  return tr[item.source_lang] || tr.en || Object.values(tr)[0] || {};
}

export default function NewsManager({ langs }) {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null); // id or "new" or null
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = useCallback(() => {
    api.getNews(true).then(setItems).catch(() => setItems([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const startNew = () => {
    setForm({ ...emptyForm, date: new Date().toISOString().slice(0, 10) });
    setEditing("new");
    setMsg("");
  };

  const startEdit = (item) => {
    const c = pick(item);
    setForm({
      title: c.title || "", summary: c.summary || "", body: c.body || "", tag: c.tag || "",
      date: item.date || "", published: item.published !== false, source_lang: item.source_lang || "en",
    });
    setEditing(item.id);
    setMsg("");
  };

  const cancel = () => { setEditing(null); setMsg(""); };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) { setMsg("Title is required."); return; }
    setBusy(true);
    setMsg("");
    try {
      if (editing === "new") await api.createNews(form);
      else await api.updateNews(editing, form);
      setEditing(null);
      load();
    } catch (e) {
      setMsg("Error: " + e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this news item? This cannot be undone.")) return;
    await api.deleteNews(id).catch(() => {});
    load();
  };

  if (items === null) {
    return <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-[#00F0FF]" size={26} /></div>;
  }

  return (
    <div>
      {!editing && (
        <>
          <div className="flex items-center justify-between mb-5">
            <p className="text-sm text-slate-400">{items.length} item(s)</p>
            <button onClick={startNew} className="cyber-btn-primary py-2 px-4 flex items-center gap-2 text-sm">
              <Plus size={15} /> New article
            </button>
          </div>

          {items.length === 0 && <p className="text-slate-500 text-sm py-10 text-center">No news yet. Create your first article.</p>}

          <div className="space-y-3">
            {items.map((item) => {
              const c = pick(item);
              return (
                <div key={item.id} className="cyber-card p-4 flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-slate-500 text-xs terminal">{item.date}</span>
                      {c.tag && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#00F0FF]/10 text-[#00F0FF]">{c.tag}</span>}
                      {item.published === false && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700 text-slate-300 flex items-center gap-1"><EyeOff size={10} /> Draft</span>}
                    </div>
                    <h3 className="text-white font-semibold truncate">{c.title}</h3>
                    {c.summary && <p className="text-slate-400 text-xs mt-1 line-clamp-2">{c.summary}</p>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => startEdit(item)} className="p-2 text-slate-400 hover:text-[#00F0FF]" title="Edit"><Pencil size={15} /></button>
                    <button onClick={() => remove(item.id)} className="p-2 text-slate-400 hover:text-red-400" title="Delete"><Trash2 size={15} /></button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {editing && (
        <div className="cyber-card p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-white font-bold">{editing === "new" ? "New article" : "Edit article"}</h3>
            <button onClick={cancel} className="text-slate-400 hover:text-white"><X size={18} /></button>
          </div>

          <div className="flex flex-wrap gap-4 mb-4">
            <div>
              <label className="block text-[11px] uppercase tracking-wide text-slate-500 mb-1">Writing language</label>
              <select value={form.source_lang} onChange={(e) => set("source_lang", e.target.value)}
                className="bg-[#0A0F2E] border border-white/10 rounded px-3 py-2 text-sm text-slate-100 outline-none">
                {langs.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wide text-slate-500 mb-1">Date</label>
              <input type="date" value={form.date} onChange={(e) => set("date", e.target.value)}
                className="bg-[#0A0F2E] border border-white/10 rounded px-3 py-2 text-sm text-slate-100 outline-none" />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wide text-slate-500 mb-1">Tag (optional)</label>
              <input type="text" value={form.tag} onChange={(e) => set("tag", e.target.value)} placeholder="e.g. Launch"
                className="bg-[#0A0F2E] border border-white/10 rounded px-3 py-2 text-sm text-slate-100 outline-none" />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-300 self-end pb-2 cursor-pointer">
              <input type="checkbox" checked={form.published} onChange={(e) => set("published", e.target.checked)} />
              {form.published ? <><Eye size={14} /> Published</> : <><EyeOff size={14} /> Draft</>}
            </label>
          </div>

          <label className="block text-[11px] uppercase tracking-wide text-slate-500 mb-1">Title</label>
          <input type="text" value={form.title} onChange={(e) => set("title", e.target.value)}
            className="w-full mb-4 bg-[#0A0F2E] border border-white/10 focus:border-[#00F0FF]/50 rounded px-3 py-2 text-sm text-slate-100 outline-none" />

          <label className="block text-[11px] uppercase tracking-wide text-slate-500 mb-1">Summary</label>
          <textarea value={form.summary} onChange={(e) => set("summary", e.target.value)} rows={2}
            className="w-full mb-4 bg-[#0A0F2E] border border-white/10 focus:border-[#00F0FF]/50 rounded px-3 py-2 text-sm text-slate-100 outline-none" />

          <label className="block text-[11px] uppercase tracking-wide text-slate-500 mb-1">Body</label>
          <textarea value={form.body} onChange={(e) => set("body", e.target.value)} rows={8}
            className="w-full mb-4 bg-[#0A0F2E] border border-white/10 focus:border-[#00F0FF]/50 rounded px-3 py-2 text-sm text-slate-100 outline-none" />

          {msg && <p className="text-xs mb-3 text-red-400">{msg}</p>}

          <div className="flex items-center gap-3">
            <button onClick={save} disabled={busy} className="cyber-btn-primary py-2.5 px-6 flex items-center gap-2">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              {busy ? "Saving & translating..." : "Save article"}
            </button>
            <button onClick={cancel} className="text-slate-400 hover:text-white text-sm">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
